if (!customElements.get('stock-category-cards')) {
  class StockCategoryCards extends HTMLElement {
    static stockRequests = new Map();

    static async hasStock(collectionURL) {
      return (await this.stockProducts(collectionURL)).size > 0;
    }

    static async stockProducts(collectionURL, condition = '') {
      if (!collectionURL) return new Set();
      const url = new URL(collectionURL, window.location.origin);
      if (url.origin !== window.location.origin) {
        throw new Error(`Cannot check stock for an external collection: ${collectionURL}`);
      }
      const key = condition ? `${url.pathname}?condition=${condition}` : url.pathname;
      if (!this.stockRequests.has(key)) {
        const request = this.fetchStock(url, condition).catch((error) => {
          this.stockRequests.delete(key);
          throw error;
        });
        this.stockRequests.set(key, request);
      }
      return this.stockRequests.get(key);
    }

    static async fetchStock(collectionURL, condition = '') {
      const url = new URL(collectionURL);
      url.pathname = `${url.pathname.replace(/\/$/, '')}/products.json`;
      url.search = '';
      url.searchParams.set('limit', '250');
      const products = new Set();
      for (let page = 1; ; page += 1) {
        url.searchParams.set('page', String(page));
        const response = await fetch(url.toString());
        if (!response.ok) {
          throw new Error(`Collection stock request failed (${response.status}): ${collectionURL.pathname}`);
        }
        const data = await response.json();
        if (
          !Array.isArray(data.products) ||
          data.products.some(
            (product) =>
              product.id == null ||
              !Array.isArray(product.variants) ||
              product.variants.some((variant) => typeof variant.available !== 'boolean')
          )
        ) {
          throw new Error(`Invalid collection stock response: ${collectionURL.pathname}`);
        }
        for (const product of data.products) {
          const conditionOption = condition
            ? product.options?.find((option) => option.name.toLowerCase() === 'condition')
            : null;
          if (product.variants.some((variant) =>
            variant.available && (!condition || (conditionOption &&
              variant[`option${conditionOption.position}`] === condition))
          )) products.add(product.id);
        }
        if (data.products.length < 250) return products;
      }
    }

    addStockBadge(card, count) {
      const badge = document.createElement('span');
      badge.className = 'category-card__stock-badge';
      badge.textContent = count.toLocaleString(document.documentElement.lang || undefined);
      badge.setAttribute('aria-label', `${badge.textContent} ${this.dataset.stockLabel}`);
      card.querySelector('.category-card__image-wrapper').append(badge);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.renderAvailableCards().catch((error) => {
        console.error('Unable to load stock-aware category cards', error);
        this.dispatchEvent(new CustomEvent('category-stock:error', { bubbles: true, detail: { error } }));
        if (this.isConnected && window.Shopify?.designMode) {
          const message = document.createElement('p');
          message.setAttribute('role', 'alert');
          message.textContent = 'Category stock could not be checked. Reload the preview to try again.';
          this.append(message);
        }
      });
    }

    async renderAvailableCards() {
      const conditionParam = 'filter.v.m.my_fields.condition';
      const fairOnly = this.dataset.fairOnly === 'true' ||
        new URLSearchParams(window.location.search).getAll(conditionParam).includes('Fair');
      const template = this.querySelector('[data-category-widget]');
      const fragment = template.content.cloneNode(true);
      const panels = new Map();
      fragment.querySelectorAll('[data-category-children]').forEach((panelTemplate) => {
        const panel = panelTemplate.content.querySelector('[data-category-panel]');
        panels.set(panel.id, panelTemplate);
      });

      const renderCard = async (cardTemplate) => {
          const card = cardTemplate.content.querySelector('.category-card');
          if (fairOnly) {
            if (cardTemplate.dataset.collectionUrl === '/collections/refurbished-phones' &&
                card.querySelector('.category-card__label').textContent.trim().toLowerCase() === 'iphone') {
              cardTemplate.dataset.collectionUrl = '/collections/iphone';
              card.href = '/collections/iphone';
            }
            const link = new URL(card.href, window.location.origin);
            if (link.origin === window.location.origin && link.pathname.includes('/collections/')) {
              for (const key of Array.from(link.searchParams.keys())) {
                if (key.startsWith('filter.') || key === 'page') link.searchParams.delete(key);
              }
              link.searchParams.set(conditionParam, 'Fair');
              card.href = `${link.pathname}${link.search}${link.hash}`;
            }
          }
          const nestedPanel = cardTemplate.content.querySelector('[data-category-children]');
          const panelTemplate = nestedPanel || panels.get(card.getAttribute('aria-controls'));
          const childProducts = new Set();
          let availableChildren = 0;
          if (panelTemplate) {
            const children = Array.from(panelTemplate.content.querySelectorAll('[data-category-child]'));
            await Promise.all(
              children.map(async (childTemplate) => {
                const products = await renderCard(childTemplate);
                if (products.size > 0) {
                  products.forEach((id) => childProducts.add(id));
                  availableChildren += 1;
                }
              })
            );
          }

          const products = availableChildren > 0
            ? childProducts
            : await StockCategoryCards.stockProducts(cardTemplate.dataset.collectionUrl, fairOnly ? 'Fair' : '');
          const hasStock = products.size > 0;
          if (!hasStock) {
            cardTemplate.remove();
            if (panelTemplate) panelTemplate.remove();
            return products;
          }
          this.addStockBadge(card, products.size);
          if (availableChildren > 0) {
            const panel = panelTemplate.content.cloneNode(true);
            if (nestedPanel) {
              cardTemplate.parentElement.closest('[data-category-panel]').append(panel);
              panelTemplate.remove();
            } else {
              panelTemplate.replaceWith(panel);
            }
          } else {
            card.removeAttribute('data-category-toggle');
            card.removeAttribute('aria-expanded');
            card.removeAttribute('aria-controls');
            if (panelTemplate) panelTemplate.remove();
          }
          cardTemplate.replaceWith(cardTemplate.content.cloneNode(true));
          return products;
      };

      await Promise.all(Array.from(fragment.querySelectorAll('[data-category-card]'), renderCard));

      if (!this.isConnected) return;
      if (!fragment.querySelector('.category-card')) {
        template.remove();
        return;
      }
      this.replaceChildren(fragment);
      const wrapper = this.querySelector('.category-cards__wrapper');
      const rootGrid = wrapper.querySelector('.category-cards__grid');
      const categoryPanels = Array.from(this.querySelectorAll('[data-category-panel]'));
      categoryPanels.forEach((panel) => wrapper.append(panel));
      const history = [];
      let currentView = rootGrid;

      const showView = (view) => {
        rootGrid.hidden = view !== rootGrid;
        categoryPanels.forEach((panel) => {
          panel.hidden = panel !== view;
        });
        currentView = view;
      };

      categoryPanels.forEach((panel) => {
        const navigation = document.createElement('div');
        navigation.className = 'category-cards__navigation';
        const back = document.createElement('button');
        back.type = 'button';
        back.className = 'category-cards__back';
        back.textContent = `\u2190 ${this.dataset.backLabel || 'Back'}`;
        navigation.append(back, panel.querySelector('.category-card__children-heading'));
        panel.prepend(navigation);
        back.addEventListener('click', () => {
          const previous = history.pop();
          if (!previous) return;
          showView(previous.view);
          previous.card.setAttribute('aria-expanded', 'false');
          previous.card.focus({ preventScroll: true });
        });
      });

      this.querySelectorAll('[data-category-toggle]').forEach((card) => {
        card.addEventListener('click', (event) => {
          event.preventDefault();
          const children = this.querySelector(`[id="${card.getAttribute('aria-controls')}"]`);
          history.push({ view: currentView, card });
          card.setAttribute('aria-expanded', 'true');
          showView(children);
          children.querySelector('.category-cards__back').focus({ preventScroll: true });
        });
      });
    }
  }
  customElements.define('stock-category-cards', StockCategoryCards);
}
